import { TutorialDefinition } from './index';

/**
 * PHASE 7: Town System & Advanced Features Tutorials
 * - Town liberation and standing system
 * - Achievement tracking system
 */

export const TOWN_SYSTEM_TUTORIAL: TutorialDefinition = {
  key: 'town-system-basics',
  title: 'Town System & Liberation',
  description: 'Learn about towns, liberation battles, standing, quests, and bonuses',
  category: 'exploration',
  priority: 'high',
  estimatedMinutes: 5,
  icon: '🏰',
  prerequisites: ['world-map-basics'],
  steps: [
    {
      id: 'welcome',
      title: 'Welcome to the Town System!',
      content: `Towns are special locations on the world map that provide powerful bonuses and fast travel options.

**5 Unique Towns:**
- ⛏️ Mining Town (Ironhold) - Ore gathering bonuses
- 🌲 Forest Town (Greenwood Haven) - Wood gathering bonuses
- 🏪 Trading Town (Crossroads Market) - Crafting bonuses
- ⚔️ Military Town (Fort Valor) - Combat bonuses
- ✨ Magical Town (Arcane Spire) - Magic bonuses

Let's explore the town system!`,
      position: 'center',
      type: 'info'
    },
    {
      id: 'town-tiles',
      title: 'Finding Towns on the Map',
      content: `Towns appear as special tiles on the hex world map with distinctive icons.

**Town Indicators:**
- 🏰 Building icon marks town locations
- Town name displayed on tile
- Boss battle indicator if not yet liberated
- Liberation status shown in tile details

Look for towns as you explore the world map!`,
      position: 'center',
      type: 'info'
    },
    {
      id: 'liberation',
      title: 'Town Liberation',
      content: `Towns start under enemy control and must be liberated through boss battles.

**Liberation Process:**
1. Find a town tile on the world map
2. Click on the town to view details
3. Click "👑 Fight Boss" to start liberation battle
4. Defeat the boss to liberate the town

**Boss Battles are Epic:**
- Larger battle arena (20x15 grid)
- Boss has +50% HP and +2 AP
- 5 enemies instead of 3
- Double material rewards on victory!`,
      position: 'center',
      type: 'info'
    },
    {
      id: 'standing',
      title: 'Town Standing System',
      content: `After liberation, you build standing (reputation) with each town.

**Standing Levels:**
- Range: 0-1000 standing points
- Levels: 0-10 (every 100 points)
- Decay: -5 points per day when inactive

**Gaining Standing:**
- Complete town quests
- Win battles near the town
- Donate materials
- Craft items (for Trading town)

Higher standing unlocks better bonuses!`,
      position: 'center',
      type: 'info'
    },
    {
      id: 'quests',
      title: 'Town Quest System',
      content: `Each town offers repeatable quests for standing and rewards.

**Quest Types:**
- ⚔️ Combat Quests - Win battles near town
- ⛏️ Gathering Quests - Collect specific materials
- 💎 Donation Quests - Donate materials to town
- 🔨 Crafting Quests - Craft specific items
- 👑 Elite Combat - Defeat boss-level enemies

Complete quests regularly to maintain and grow your standing!`,
      position: 'center',
      type: 'info'
    },
    {
      id: 'bonuses',
      title: 'Town Bonus System',
      content: `Liberated towns provide powerful global bonuses to all your characters.

**Bonus Tiers:**
- Each town has 3 bonus slots
- Each slot has 3 tiers (9 total bonuses per town)
- Bonuses unlock at standing levels 1-9
- Level 10 provides buffer against decay

**Bonus Types (by Town):**
- Mining: +Ore gathering, +Vitality
- Forest: +Wood gathering, +Agility
- Trading: +Crafting success, +Gold
- Military: +Combat stats, +XP
- Magical: +Mana, +Intelligence

Bonuses are always active when standing ≥ 100!`,
      position: 'center',
      type: 'info'
    },
    {
      id: 'fast-travel',
      title: 'Fast Travel System',
      content: `Liberated towns enable fast travel between towns at reduced TP cost.

**Fast Travel Benefits:**
- Travel from any liberated town to another
- Costs only 25% of normal TP (75% discount!)
- Must be standing in a town to use
- Instantly teleports to destination town

**Example:**
- Normal travel: 10 TP
- Fast travel: 2-3 TP

Use fast travel to efficiently navigate the world!`,
      position: 'center',
      type: 'info'
    },
    {
      id: 'town-progression',
      title: 'Town Progression Strategy',
      content: `**Early Game (Levels 1-10):**
- Liberate your first town for bonuses
- Focus on nearby towns for your class
- Warriors → Mining Town
- Rogues → Forest Town
- Mages → Magical Town

**Mid Game (Levels 11-20):**
- Liberate Trading Town for crafting bonuses
- Complete daily quests to build standing
- Aim for Level 5 standing in main towns

**Late Game (Levels 21+):**
- Liberate all 5 towns
- Maintain Level 8+ standing everywhere
- Maximize all bonus tiers
- Use fast travel network efficiently`,
      position: 'center',
      type: 'info'
    },
    {
      id: 'town-details',
      title: 'Viewing Town Information',
      content: `Click on any town tile to view detailed information:

**Town Modal Shows:**
- Liberation status and boss name
- Current standing and level
- Progress bar to next level
- Available quests with requirements
- Active bonuses from your standing
- Fast travel options and costs

Check town details regularly to track your progress!`,
      position: 'center',
      type: 'info'
    },
    {
      id: 'complete',
      title: 'Town System Complete!',
      content: `You now understand the town system!

**Key Takeaways:**
✅ Find and liberate towns via boss battles
✅ Build standing through quests and activities
✅ Unlock powerful global bonuses (9 per town)
✅ Use fast travel to navigate efficiently
✅ Maintain standing to prevent decay

**Next Steps:**
1. Find a town on the world map
2. Liberate it by defeating the boss
3. Complete quests to build standing
4. Enjoy your global bonuses!

Good luck, and may your towns prosper! 🏰`,
      position: 'center',
      type: 'info'
    }
  ]
};

export const ACHIEVEMENTS_TUTORIAL: TutorialDefinition = {
  key: 'achievements-basics',
  title: 'Achievement System',
  description: 'Learn about tracking achievements, earning rewards, and viewing progress',
  category: 'general',
  priority: 'medium',
  estimatedMinutes: 3,
  icon: '🏆',
  prerequisites: [],
  steps: [
    {
      id: 'welcome',
      title: 'Welcome to Achievements!',
      content: `The achievement system tracks your accomplishments and rewards your progress.

**28 Achievements** across 5 categories:
- ⚔️ Battle - Combat victories
- 💪 Combat - Damage and kills
- 🔨 Crafting - Item creation
- 🗺️ Exploration - World discovery
- ⛏️ Gathering - Resource collection

Let's explore achievements!`,
      position: 'center',
      type: 'info'
    },
    {
      id: 'accessing',
      title: 'Accessing Achievements',
      content: `You can view achievements from multiple locations:

**Navigation:**
1. Click "Achievements" in the main navigation
2. Or click the 🏆 icon on the home dashboard
3. Achievement page shows all achievements

**Page Features:**
- Category filter tabs
- Progress tracking
- Locked/unlocked status
- Completion statistics`,
      targetSelector: 'nav button',
      position: 'bottom',
      type: 'info'
    },
    {
      id: 'categories',
      title: 'Achievement Categories',
      content: `Achievements are organized into 5 categories:

**⚔️ Battle (4 achievements):**
- First Blood, Veteran Warrior, Battle Master, Legendary Conqueror
- Track total battles won

**💪 Combat (5 achievements):**
- Monster Slayer, Heavy Hitter, Devastating Force, etc.
- Track monsters defeated and damage dealt

**🔨 Crafting (7 achievements):**
- Apprentice Crafter → Legendary Smith
- Recipe Seeker → Master Chef
- Track items crafted and recipes discovered`,
      position: 'center',
      type: 'info'
    },
    {
      id: 'progress',
      title: 'Tracking Progress',
      content: `Each achievement shows your current progress:

**Progress Display:**
- Current value / Required value (e.g., 5 / 10 battles)
- Progress bar showing completion percentage
- Description of what you need to do
- Icon indicating category

**Achievement States:**
- 🔒 Locked - Not yet earned (gray)
- ✅ Unlocked - Completed (gold border)
- 📊 In Progress - Partially complete (blue bar)

Track your closest achievements and work toward them!`,
      position: 'center',
      type: 'info'
    },
    {
      id: 'notifications',
      title: 'Achievement Notifications',
      content: `When you unlock an achievement, you'll see a notification!

**Notification Features:**
- Toast popup in top-right corner
- Achievement icon and name
- Brief congratulatory message
- Progress indicator
- Auto-dismisses after 5 seconds

Keep playing to unlock more achievements!`,
      position: 'center',
      type: 'info'
    },
    {
      id: 'stats',
      title: 'Player Statistics',
      content: `The achievements page shows your overall stats:

**Statistics Tracked:**
- Total battles won
- Total monsters defeated
- Total damage dealt
- Items crafted
- Recipes discovered
- Tiles explored
- Materials gathered

These stats feed into achievement progress!`,
      position: 'center',
      type: 'info'
    },
    {
      id: 'strategy',
      title: 'Achievement Strategy',
      content: `**Early Game Focus:**
- First Blood (1 battle) - Easy starter
- Resource Gatherer (100 materials)
- First Steps (5 tiles explored)
- Apprentice Crafter (5 items)

**Mid Game Goals:**
- Veteran Warrior (50 battles)
- Monster Slayer (100 monsters)
- Skilled Artisan (25 items)
- Adventurer (25 tiles)

**Late Game Challenges:**
- Legendary Conqueror (500 battles)
- Unstoppable Power (100,000 damage)
- Master Chef (50 recipes)
- World Explorer (100 tiles)

Work toward achievements naturally through gameplay!`,
      position: 'center',
      type: 'info'
    },
    {
      id: 'complete',
      title: 'Achievement System Complete!',
      content: `You now understand the achievement system!

**Key Takeaways:**
✅ 28 achievements across 5 categories
✅ Progress tracked automatically
✅ Notifications when unlocking
✅ View stats and completion percentage
✅ Work toward achievements naturally

**Next Steps:**
1. Open the Achievements page
2. Review available achievements
3. Check your current progress
4. Set goals for next achievements!

Good luck on your achievement journey! 🏆`,
      position: 'center',
      type: 'info'
    }
  ]
};
